import { Component, Input, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Subject } from 'rxjs';
import {
  ChannelNotificationSettings,
  ChannelsCascadeSettings,
  ConfiguredChannel,
  MassNotification,
} from 'src/app/modules/communications/store/communications/communications.model';
import { ChannelSelectionService } from 'src/app/modules/communications/services/channel-selection-service';
import DataLabelsPlugin from 'src/app/modules/communications/components/notification/charts/plugins/data-labels';
import {
  BarChartDataItem,
  BarChartXAxis,
  BarChartYAxis,
  ChartWidgetLabelSettings,
  barChartPointRadius
} from 'src/app/modules/communications/components/notification/charts/chart.models';
import { CultureService } from '@app/modules/core/services/culture.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'topo-comms-notification-timeline-chart-widget',
    templateUrl: './notification-timeline-chart-widget.component.html',
    styleUrls: ['./notification-timeline-chart-widget.component.scss'],
    standalone: false
})
export class NotificationTimelineChartWidgetComponent implements OnInit, OnDestroy {
  data: BarChartDataItem[];
  countLabel: string;
  showLegend: boolean = true;
  showPercentage: boolean = false;
  dataLabel?: string;
  colorPerBar: boolean = true;
  xGap = 1;
  chartType: string = 'bar';
  colors?: any = {
    axisColor: '#7257FF',
    labelColor: '#f0f0f0',
    titleColor: '#f0f0f0'
  };
  private destroy$ = new Subject<void>();

  @Input() width: string;

  maxStopTime: number;
  channelsCascadeSettings: ChannelsCascadeSettings;

  @Input() set notification(value: MassNotification) {
    this.data = [];
    if (!!!value?.channelsCascadeSettings.channelNotifications) {
      return;
    }
    this.maxStopTime = 0;

    this.channelsCascadeSettings = value?.channelsCascadeSettings;
    value?.channelsCascadeSettings.channelNotifications?.forEach(c => {
      if (c.stopTimeMinutes > this.maxStopTime) {
        this.maxStopTime = c.stopTimeMinutes;
      }
    });


    this.xGap = 3;
    value?.channelsCascadeSettings.channelNotifications?.forEach(c => {
      const data = {
        id: c.uId,
        data: [],
        label: this.getChannelName(c.configuredChannelUId),
        label2: this.getChannelSettingsDisplay(c),
      };
      data.data.push({
        id: c.uId,
        from: c.startTimeMinutes,
        to: c.resendTimeMinutes > 0 ? c.stopTimeMinutes : this.maxStopTime,
        color: c.resendTimeMinutes > 0 ? '#EE6400' : '#917AFF',
        textColor: '#404040',
        width: 8,
        label: this.getChannelName(c.configuredChannelUId),
        label2: this.getChannelSettingsDisplay(c)
      });

      this.data.push(data);
    });

    this.height = ((this.data.length * 48) + 48) + 'px';
    this.applyData(this.data);
  }

  height: string;
  yAxis: Array<BarChartYAxis>;
  xAxis: Array<BarChartYAxis>;
  private pointRadius = barChartPointRadius;
  private maxValue: any;
  public barChartPlugins = [DataLabelsPlugin];

  public notificationSegments: { width: string; color: string }[];

  _data: Array<BarChartDataItem>;
  types = ['bar'];
  chartData: {
    labels: string[] | undefined[];
    labels2: string[] | undefined[];
    datasets: Array<{ type: string; data: any[]; backgroundColor: string[]; order: number }[] | undefined[]>;
  };

  @ViewChild('canvas') canvas: ElementRef;

  constructor(
    private channelSelectionService: ChannelSelectionService,
    private cultureService: CultureService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.countLabel = this.translateService.instant('COMMUNICATIONS.MINUTES');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyData(value: Array<BarChartDataItem>): void {
    this._data = value;

    if (value && value.length > 0) {
      const tempData = {
        labels: value.map(v => v.label),
        labels2: value.map(v => v.label2),
        datasets: []
      };

      let index = 0;

      this.types?.forEach(type => {
        const dataset = {
          type: type,
          data: [],
          order: index + 1,
          total: 0
        };

        dataset.data = [];
        value.forEach(v => {
          if (v.data[index]?.from !== undefined || v.data[index]?.to !== undefined) {
            const element: any[] = [];

            element.push(v.data[index]?.from);
            element.push(v.data[index]?.to);
            element.push(v.label2);

            dataset.data.push(element);
          } else {
            dataset.data.push(v.data[index].count);
          }
        });

        let total = 0;
        value.map(v => (total += v.data[index].count));
        dataset.total = total;

        this.maxValue = 0;
        value.map(v => {
          if (v.data[index].count > this.maxValue) this.maxValue = v.data[index].count;
        });

        const color = value[0].data[index].color !== null ? value[0].data[index].color : undefined;

        if (type === 'bar') {
          dataset['barThickness'] = value[0].data[index].width !== null ? value[0].data[index].width : undefined;
          if (this.colorPerBar === true) {
            dataset['backgroundColor'] = value.some(v => v.data[0].color != null) ? value.map(v => v.data[0].color) : undefined;
          } else {
            dataset['backgroundColor'] = color;
          }
          dataset['borderRadius'] = {
            topLeft: 4,
            topRight: 4,
            bottomLeft: 4,
            bottomRight: 4
          };
        } else {
          if (this.colorPerBar === true) {
            const barColor = value.some(v => v.data[0].color != null) ? value.map(v => v.data[0].color) : undefined;
            dataset['borderColor'] = '#808080';
            dataset['borderWidth'] = 2;
            dataset['pointBackgroundColor'] = barColor;
          } else {
            dataset['borderColor'] = '#808080';
            dataset['borderWidth'] = 2;
            dataset['pointBackgroundColor'] = color;
          }
          dataset['pointRadius'] = this.pointRadius;
          dataset['fill'] = false;
        }

        index++;
        tempData.datasets.push(dataset);
      });

      this.chartData = tempData;
    } else {
      this.chartData = {
        labels: [],
        labels2: [],
        datasets: []
      };
    }
  }

  get chartOptions(): any {
    return {
      indexAxis: 'y',
      responsive: true,
      animation: false,
      maintainAspectRatio: false,
//      events: [], // this disables the hovers
      plugins: {
        tooltip: {
          callbacks: {
            label: context => {
              let label = context.label || '';

              if (label) {
               if (!!context.raw[2]) {
                  label = context.raw[2];
                }
              }
              return label;
            }
          }
        },
        legend: {
          display: false
        },
        datalabels: {
          display: false,
          anchor: 'top',
          align: 'top',
          color: this.colors?.labelColor,
          formatter: (value, context) => {
            return value?.length > 2 ? value[2] : null;
          },
          clamp: false,
          clip: true,
          offset: 0
        }
      },
      scales: {
        x: {
          type: 'linear',
          position: 'bottom',
          beginAtZero: true,
          offset: false,
          stacked: false,
          suggestedMax: () => {
            return this.maxValue * 1.5;
          },
          grid: {
            color: this.colors?.axisColor,
            display: true,
            drawOnChartArea: false
          },
          ticks: {
            color: this.colors?.titleColor,
            display: true,
            stepSize: 15,
          },
          title: { display: true, text: this.countLabel, color: this.colors?.titleColor }
        },
        y: {
          offset: true,
          stacked: false,
          grid: {
            color: this.colors?.axisColor,
            display: false,
            drawOnChartArea: false
          },
          ticks: {
            color: this.colors?.titleColor
          },
          title: { display: false, text: this.dataLabel, color: this.colors?.titleColor }
        }
      }
    };
  }

  chartClick(event: any): void {}

  private getChannelName(uid: string): string {
    const channel = this.channelSelectionService.availableChannels?.find(c => c.uId === uid);
    if (!!channel) {
      return channel.name;
    } else {
      return null;
    }
  }

  private getChannelSettingsDisplay(settings: ChannelNotificationSettings): string {
    return this.channelSelectionService.getChannelSettingsDisplay(settings);
  }

  selected(data: { item: BarChartDataItem; subValue: any }): void {}
}
